import React, { useState } from "react";
import BrandLogo from "../common/BrandLogo";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const GD = "#005885";
const BG = "#F3F2EF";
const CARD = "#FFFFFF";
const B = "#E1E9EE";

/**
 * Login / "Welcome back" page.
 * LinkedIn OAuth primary, magic-link email as backup.
 */
export default function LoginPage({ onLogin, onDemoLogin }) {
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [stagingLink, setStagingLink] = useState("");
  const [linkError, setLinkError] = useState("");

  const handleSendLink = () => {
    if (!email) return;
    setLinkError("");
    fetch(`${API_BASE}/app/api/auth/request-link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.status === "ok") {
          setMagicSent(true);
          if (d.staging_link) setStagingLink(d.staging_link);
        } else {
          setLinkError(d.error || "Something went wrong");
        }
      })
      .catch(() => setLinkError("Network error — please try again"));
  };

  const handleLinkedIn = () => {
    const tempId = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.location.href = `${API_BASE}/app/api/auth/linkedin/connect?temp_id=${tempId}`;
  };

  // ── Email sent confirmation ──
  if (magicSent) {
    return (
      <div style={{ background: BG, minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: "'Playfair Display',Georgia,serif", color: "#1A1A1A" }}>
        <style>{`:root{--blue:#0077B5;--bg:#F3F2EF;} *{box-sizing:border-box;margin:0;padding:0;} body{background:#F3F2EF;-webkit-font-smoothing:antialiased;} input{outline:none;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} input::placeholder{color:#999;}`}</style>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="fade-up" style={{ width: "100%", maxWidth: 400, padding: "0 24px", textAlign: "center" }}>
            <BrandLogo size="lg" showSub={true} style={{ flexDirection: "column", textAlign: "center" }} />
            <div style={{ fontSize: 48, margin: "32px 0 20px" }}>📬</div>
            <div style={{ fontSize: 30, marginBottom: 10 }}>Check your inbox.</div>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16, color: "#666666", lineHeight: 1.7, marginBottom: stagingLink ? 20 : 36 }}>
              We sent a login link to<br /><span style={{ color: G }}>{email}</span><br />It expires in 30 minutes.
            </div>
            {stagingLink && (
              <div style={{ background: "#F3F2EF", border: "1px solid #3a3000", borderRadius: 9, padding: "14px 16px", marginBottom: 24 }}>
                <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#005885", letterSpacing: "0.1em", marginBottom: 8 }}>STAGING · MAGIC LINK</div>
                <a href={stagingLink} style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: G, wordBreak: "break-all", lineHeight: 1.6, display: "block" }}>{stagingLink}</a>
              </div>
            )}
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#999999" }}>
              Didn't get it? <span style={{ color: GD, cursor: "pointer" }} onClick={() => setMagicSent(false)}>Resend</span>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Email form (backup) ──
  if (showEmail) {
    return (
      <div style={{ background: BG, minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: "'Playfair Display',Georgia,serif", color: "#1A1A1A" }}>
        <style>{`:root{--blue:#0077B5;--bg:#F3F2EF;} *{box-sizing:border-box;margin:0;padding:0;} body{background:#F3F2EF;-webkit-font-smoothing:antialiased;} input{outline:none;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} input::placeholder{color:#999;}`}</style>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="fade-up" style={{ width: "100%", maxWidth: 400, padding: "0 24px" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 48 }}>
              <BrandLogo size="lg" showSub={true} style={{ flexDirection: "column", textAlign: "center" }} />
            </div>
            <div style={{ fontSize: 40, lineHeight: 1.2, textAlign: "center", marginBottom: 6 }}>Welcome<br /><em>back.</em></div>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16, color: "#666666", textAlign: "center", marginBottom: 40 }}>Enter your email and we'll send you a login link</div>
            <div style={{ marginBottom: 14 }}>
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSendLink()}
                placeholder="your@email.com"
                type="email"
                style={{ width: "100%", background: CARD, border: `1px solid ${B}`, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "15px 16px", color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16, minHeight: 44 }}
              />
            </div>
            <button
              onClick={handleSendLink}
              disabled={!email}
              style={{ width: "100%", background: email ? G : "#EBF5FB", color: email ? BG : "#999999", border: "none", borderRadius: 10, padding: "15px", fontSize: 16, cursor: email ? "pointer" : "not-allowed", fontFamily: "'Playfair Display',Georgia,serif", transition: "all 0.2s", minHeight: 44 }}
            >
              Send login link →
            </button>
            {linkError && <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#c05050", marginTop: 10, textAlign: "center" }}>{linkError}</div>}
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <span onClick={() => setShowEmail(false)} style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: G, cursor: "pointer" }}>← Back</span>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Main login: LinkedIn primary ──
  return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: "'Playfair Display',Georgia,serif", color: "#1A1A1A" }}>
      <style>{`:root{--blue:#0077B5;--bg:#F3F2EF;} *{box-sizing:border-box;margin:0;padding:0;} body{background:#F3F2EF;-webkit-font-smoothing:antialiased;} input{outline:none;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} input::placeholder{color:#999;}`}</style>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="fade-up" style={{ width: "100%", maxWidth: 400, padding: "0 24px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 48 }}>
            <BrandLogo size="lg" showSub={true} style={{ flexDirection: "column", textAlign: "center" }} />
          </div>

          <div style={{ fontSize: 40, lineHeight: 1.2, textAlign: "center", marginBottom: 6 }}>Welcome<br /><em>back.</em></div>
          <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16, color: "#666666", textAlign: "center", marginBottom: 40 }}>Sign in to your BrandHives account</div>

          <button
            onClick={handleLinkedIn}
            style={{ width: "100%", background: G, color: BG, border: "none", borderRadius: 10, padding: "15px 20px", fontSize: 16, cursor: "pointer", fontFamily: "'Playfair Display',Georgia,serif", transition: "background 0.2s", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            onMouseOver={e => e.currentTarget.style.background = GD}
            onMouseOut={e => e.currentTarget.style.background = G}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            Sign in with LinkedIn
          </button>

          <div style={{ textAlign: "center", marginTop: 28 }}>
            <span onClick={() => setShowEmail(true)} style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, color: "#666", cursor: "pointer" }}>or <span style={{ color: G }}>sign in with email</span></span>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <div style={{ background: "#fff", borderTop: "1px solid #E1E9EE", padding: "24px 32px", display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 16, width: "100%" }}>
      <BrandLogo size="footer" showSub={true} href="https://brandhives.com" />
      <div style={{ display: "flex", gap: 24 }}>
        <a href="#/privacy-policy" style={{ fontSize: 13, color: "#999", textDecoration: "none" }}>Privacy</a>
        <a href="#/terms" style={{ fontSize: 13, color: "#999", textDecoration: "none" }}>Terms</a>
        <a href="mailto:operator@bhive.ca" style={{ fontSize: 13, color: "#999", textDecoration: "none" }}>Contact</a>
      </div>
    </div>
  );
}
